import React, { useState, useEffect } from 'react';
import {
  Users,
  Award,
  Target,
  BookOpen,
  Globe,
  CheckCircle,
  XCircle,
  ArrowRight,
  Lightbulb,
  TrendingUp,
  Heart,
  Shield,
  FileText,
  ExternalLink,
  MessageSquare,
  UserCheck,
  Building,
  Network,
  Loader2,
  User,
  Mail,
  Send
} from 'lucide-react';
import { useFormSubmit } from '../../hooks/useFormSubmit';

const RedUNADI = () => {
  // Estado para datos dinámicos
  const [redData, setRedData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Form state management
  const [formData, setFormData] = useState({
    tipo: 'docente',
    nombre: '',
    email: '',
    linea: '',
    institucion: '',
    mensaje: '',
    renovacionMembresia: false,
    numeroMembresia: '',
    acepto: false
  });
  const [formErrors, setFormErrors] = useState({});

  const { loading: isSubmitting, success: isSuccess, error: submitError, submitForm, resetForm: resetFormApi } = useFormSubmit('red-unadi');

  // Cargar datos del JSON
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [redResponse, generalesResponse] = await Promise.all([
          fetch(`/data/redunadi.json?v=${Date.now()}`, { cache: 'no-store' }),
          fetch(`/data/generales.json?v=${Date.now()}`, { cache: 'no-store' })
        ]);

        const redData = await redResponse.json();
        const generalesData = await generalesResponse.json();

        setRedData(redData);
        setDatosBancarios(generalesData.datosBancarios);
      } catch (error) {
        console.error('Error al cargar los datos de Red UNADI:', error);
      } finally {
        setIsLoading(false);
      }
    };

    cargarDatos();
  }, []);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitForm(formData);
  };

  const resetForm = () => {
    setFormData({
      tipo: 'docente',
      nombre: '',
      email: '',
      linea: '',
      institucion: '',
      mensaje: '',
      renovacionMembresia: false,
      numeroMembresia: '',
      acepto: false
    });
    resetFormApi();
  };

  // Auto reset form on success (same behaviour as RedRiel)
  useEffect(() => {
    if (isSuccess) {
      resetForm();
    }
  }, [isSuccess]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información de Red UNADI...</p>
        </div>
      </div>
    );
  }

  if (!redData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Error al cargar los datos de Red UNADI</p>
        </div>
      </div>
    );
  }

  // Función para obtener iconos dinámicamente
  const getIconComponent = (iconName) => {
    const icons = {
      Globe,
      TrendingUp,
      Heart,
      Users,
      FileText,
      Award,
      Target,
      Lightbulb,
      Building,
      Shield,
      User
    };
    return icons[iconName] || Globe;
  };

  const derechos = [
    'Recibir información sobre las actividades, proyectos y oportunidades de colaboración dentro de la Red.',
    'Proponer iniciativas, proyectos o líneas de investigación en el marco de la educación.',
    'Decidir libremente su participación en las actividades y proyectos promovidos por la Red, sin que ello implique un compromiso obligatorio.',
    'Acceder a publicaciones, informes y resultados generados por la Red.',
    'Ser reconocidos como miembros en la Red, así como en las iniciativas y productos derivados de la Red en función de su nivel de implicación.'
  ];

  const obligaciones = [
    'Designar una persona de contacto que facilite la comunicación entre el grupo de investigación y la Red Unadi.',
    'Respetar la misión y objetivo de la red desde los principios éticos que promulga la UNESCO (https://www.unesco.org/es/ethics).',
    'Favorecer la cooperación interdisciplinar e interinstitucional en las iniciativas impulsadas por la Red; y siempre desde la libertad de participar o no en las correspondientes iniciativas.'
  ];

  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50',
    green: 'border-[#0b742f] bg-green-50',
    purple: 'border-purple-500 bg-purple-50',
    orange: 'border-[#e87729] bg-orange-50',
    red: 'border-red-500 bg-red-50'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Users className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Red UNADI
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-2">
                Red de Cátedras UNESCO para el Aprendizaje y Desarrollo de Iberoamérica
              </p>
              <p className="text-lg text-white/80">
                Interactividad y cooperación entre Cátedras UNESCO en Iberoamérica
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Logos Red UNADI */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-200 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0077C8]/5 via-transparent to-[#0b742f]/5"></div>
            <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
              {/* Logo Red UNADI */}
              <div className="group flex flex-col items-center">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 border-2 border-transparent hover:border-[#0077C8]/20">
                  <img
                    src="https://recursos.cenid.org/img_cenid/redunadi.png"
                    alt="Red UNADI"
                    className="h-24 md:h-32 w-auto object-contain"
                  />
                </div>
                <span className="mt-3 text-sm font-semibold text-gray-600 group-hover:text-[#0077C8] transition-colors text-center"></span>
              </div>

              {/* Separator with icon */}
              <div className="flex items-center justify-center">
                <div className="hidden md:flex flex-col items-center gap-2">
                  <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#0077C8] to-transparent"></div>
                  <div className="bg-[#e87729] p-2 rounded-full shadow-lg">
                    <Network className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-px h-12 bg-gradient-to-b from-[#e87729] via-transparent to-transparent"></div>
                </div>
                <div className="md:hidden w-32 h-px bg-gradient-to-r from-transparent via-[#0077C8] to-transparent"></div>
              </div>

              {/* Logo Red */}
              <div className="group flex flex-col items-center">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 border-2 border-transparent hover:border-[#0b742f]/20">
                  <img
                    src="https://recursos.cenid.org/img_cenid/logored.png"
                    alt="Logo Red"
                    className="h-24 md:h-32 w-auto object-contain"
                  />
                </div>
                <span className="mt-3 text-sm font-semibold text-gray-600 group-hover:text-[#0b742f] transition-colors text-center"></span>
              </div>
            </div>
          </div>
        </section>

        {/* Descripción */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#0b742f]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Globe className="w-8 h-8 text-[#0b742f]" />
                ¿Qué es Red UNADI?
              </h2>
              <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
            </div>
            <p className="text-gray-700 leading-relaxed text-justify mb-4">
              La Red de Cátedras UNESCO para el Aprendizaje y Desarrollo de Iberoamérica es una iniciativa dedicada a la interactividad y cooperación entre Cátedras UNESCO en Iberoamérica que se esfuercen por garantizar una educación inclusiva, equitativa y de calidad, promoviendo oportunidades de aprendizaje durante toda la vida para todos.
            </p>
            <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                La idea de creación de esta RED surge al finalizar el Foro Iberoamericano de Cátedras UNESCO organizado por el Centro de Estudios e Investigaciones para el Desarrollo Docente CENID A.C. y forma parte para la postulación del Proyecto Cátedra sobre análisis e inclusión del conocimiento.
              </p>
            </div>
          </div>
        </section>

        {/* Propósito */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#e87729]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Target className="w-8 h-8 text-[#e87729]" />
                Propósito
              </h2>
              <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed text-justify">
                Con esta iniciativa se busca movilizar la inteligencia colectiva y la experiencia de la red de Cátedras UNESCO de toda Iberoamérica para intercambiar sobre cuestiones emergentes relacionadas con la temática de la educación superior en el marco del ODS 4. A través de debates interdisciplinarios y multidisciplinarios, el encuentro tiene como objetivo ampliar los límites del conocimiento sobre temas relacionados con el mandato de la UNESCO en educación, cultura, comunicación y ciencias.
              </p>
              <div className="bg-orange-50 border-l-4 border-[#e87729] p-6 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed">
                  A través de un enfoque multidisciplinario, se pretende establecer sinergias y colaboraciones con diversas cátedras, grupos de investigación, expertos, instituciones educativas para generar conocimiento, análisis de buenas prácticas y políticas informadas que contribuyan a compartir conocimientos, recursos y experiencias, con el fin de mejorar la calidad y la innovación en la educación.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Misión y Objetivo */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Lightbulb className="w-8 h-8 text-[#0b742f]" />
                  Misión y objetivo
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Heart className="w-6 h-6 text-[#0b742f]" />
                    Misión
                  </h3>
                  <p className="text-gray-700">
                    Esta red pretende movilizar la inteligencia colectiva y la experiencia de la red de Cátedras UNESCO de toda Iberoamérica para intercambiar sobre cuestiones emergentes relacionadas con la temática de la educación superior. A través de debates interdisciplinarios y multidisciplinarios, la red tiene como objetivo ampliar los límites del conocimiento sobre temas relacionados con el mandato de la UNESCO en educación, cultura, comunicación y ciencias.
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="w-6 h-6 text-blue-600" />
                    Objetivo
                  </h3>
                  <p className="text-gray-700">
                    Esta red se plantea como objetivo impulsar la investigación, la formación y la implementación y desarrollar en la región la capacidad de acción orientada hacia la inclusión, integración y complementariedad de iniciativas internacionales de intercambio de conocimiento.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Iniciativas UNESCO */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#e87729]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8 text-[#e87729]" />
                  Iniciativas colaborativas impulsadas por la UNESCO
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
              </div>

              <div className="space-y-4">
                {redData.iniciativasUNESCO.map((iniciativa, index) => {
                  const IconComponent = getIconComponent(iniciativa.icono);
                  return (
                    <div
                      key={index}
                      className={`border-l-4 ${colorClasses[iniciativa.color]} p-6 rounded-r-lg hover:shadow-md transition-shadow duration-200`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <IconComponent className="w-6 h-6 text-gray-700" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{iniciativa.titulo}</h3>
                          <p className="text-gray-700">{iniciativa.descripcion}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Organización */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#0b742f]">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Building className="w-8 h-8 text-[#0b742f]" />
                Organización
              </h2>
              <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
            </div>

            <div className="space-y-4">

              {/* Comité Editorial */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-blue-600" />
                  Comité Directivo
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {redData.organizacion.comiteEditorial.map((miembro, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-4 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-full shadow-sm">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-900 mb-1">{miembro.nombre}</h5>
                          <p className="text-sm text-gray-600 mb-1">{miembro.pais}</p>
                          <p className="text-sm font-semibold text-blue-600">{miembro.cargo}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>


              {/*
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <p className="text-gray-700">
                  Las Cátedras UNESCO / individuos e instituciones / grupos de investigación / asociaciones que se sumen a dicha red serán miembros de la misma.
                </p>
              </div>

              <div className="bg-orange-50 border-l-4 border-[#e87729] p-6 rounded-r-lg">
                <p className="text-gray-700">
                  Desde la dirección se podrán nombrar otras figuras y personas que las representen a fin de organizar coordinaciones de acciones o ámbitos.
                </p>
              </div>
              */}

            </div>
          </div>
        </section>

        {/* Obligaciones y Derechos 
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#e87729]">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Shield className="w-8 h-8 text-[#e87729]" />
                  Obligaciones y derechos de los miembros
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
              </div>

              <div className="mb-6 bg-gray-50 border-l-4 border-gray-400 p-6 rounded-r-lg">
                <p className="text-gray-700">
                  Los miembros que formen parte de la Red de Cátedras UNESCO para el Aprendizaje y Desarrollo de Iberoamérica tendrán un rol <strong>consultivo y colaborativo</strong>. Su participación será voluntaria y flexible, respetando su autonomía y líneas de trabajo. Asimismo, podrán abandonar cuando lo deseen esta red.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-[#0b742f]" />
                    Derechos
                  </h3>
                  <div className="space-y-3">
                    {derechos.map((derecho, index) => (
                      <div key={index} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-1" />
                        <span className="text-gray-700 text-sm">{derecho}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-[#e87729]" />
                    Obligaciones
                  </h3>
                  <div className="space-y-3">
                    {obligaciones.map((obligacion, index) => (
                      <div key={index} className="flex items-start gap-3 bg-orange-50 p-4 rounded-lg">
                        <ArrowRight className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-1" />
                        <span className="text-gray-700 text-sm">{obligacion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        */}

        {/* Registro a la Red UNADI */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <UserCheck className="w-8 h-8 text-[#0b742f]" />
                  Registro a la Red UNADI
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
                <p className="text-gray-600 mt-4">
                  La Red de Cátedras UNESCO para el Aprendizaje y Desarrollo de Iberoamérica es una iniciativa dedicada a la interactividad y cooperación entre Cátedras UNESCO en Iberoamérica que se esfuercen por garantizar una educación inclusiva, equitativa y de calidad, promoviendo oportunidades de aprendizaje durante toda la vida para todos.
                </p>
              </div>

              {/* Membresías */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">MEMBRESÍAS</h3>

                {/* ¿Quiénes pueden ser parte? */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 mb-8 border-l-4 border-[#e87729]">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">
                    ¿Quiénes pueden ser parte de nuestra red?
                  </h4>
                  <p className="text-gray-700 text-lg">
                    {redData.membresias.quienesPueden}
                  </p>
                </div>

                {/* Tipos de Membresías 
                  <div className="space-y-6 mb-8">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                      <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-blue-600" />
                        • INDIVIDUAL
                      </h4>
                      <p className="text-gray-700 italic">
                        {redData.membresias.individual}
                      </p>
                    </div>

                    <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
                      <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#0b742f]" />
                        • COLECTIVA (Grupo de Investigación/Institución/Universidad/Asociación, etc.)
                      </h4>
                      <p className="text-gray-700 italic">
                        {redData.membresias.colectiva}
                      </p>
                    </div>
                  </div>
                */}

                {/* Beneficios */}
                <h4 className="text-2xl font-bold text-[#0b742f] mb-4">Beneficios de ser miembro</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {redData.membresias.beneficios.map((beneficio, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:border-[#0b742f] hover:shadow-md transition-all duration-200"
                    >
                      <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{beneficio}</span>
                    </div>
                  ))}
                </div>

                {/* Precio e Inversión con Datos Bancarios */}
                <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg p-6 mb-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                    <div>
                      <h4 className="text-2xl font-bold mb-2">Pago anual por integrante</h4>
                      <p className="text-white/90">{redData.membresias.inversion.descripcion}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-5xl font-bold">${redData.membresias.inversion.monto}</div>
                      <p className="text-white/90 text-sm mt-1">{redData.membresias.inversion.moneda}</p>
                    </div>
                  </div>

                  {datosBancarios && (
                    <div className="border-t border-white/20 pt-6">
                      <h5 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Building className="w-5 h-5" />
                        Datos para transferencia bancaria
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-white/70 mb-1">Beneficiario</p>
                          <p className="font-semibold">{datosBancarios.titular}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-white/70 mb-1">Banco</p>
                          <p className="font-semibold">{datosBancarios.banco}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-white/70 mb-1">Cuenta</p>
                          <p className="font-semibold font-mono">{datosBancarios.numeroCuenta}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-white/70 mb-1">CLABE Interbancaria</p>
                          <p className="font-semibold font-mono">{datosBancarios.clabe}</p>
                        </div>
                        {datosBancarios.sucursal && (
                          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:col-span-2">
                            <p className="text-white/70 mb-1">Sucursal</p>
                            <p className="font-semibold">{datosBancarios.sucursal}</p>
                          </div>
                        )}
                        {datosBancarios.swift && (
                          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <p className="text-white/70 mb-1">SWIFT</p>
                            <p className="font-semibold font-mono">{datosBancarios.swift}</p>
                          </div>
                        )}
                      </div>
                      {redData?.contacto?.correo && (
                        <div className="bg-white/15 border-l-4 border-white/60 p-4 rounded mt-4">
                          <p className="text-sm text-white">
                            <strong>Importante:</strong> Envía tu comprobante de pago al correo{' '}
                            <a
                              href={`mailto:${redData.contacto.correo}`}
                              className="underline font-semibold"
                            >
                              {redData.contacto.correo}
                            </a>
                            {' '}para confirmar tu inscripción.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Contacto */}
                <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">{redData.contacto.mensajeInformes}</p>
                      <a href={`mailto:${redData.contacto.correo}`} className="text-blue-600 font-bold text-lg hover:underline">
                        {redData.contacto.correo}
                      </a>
                    </div>
                  </div>
                </div>

              </div>

              {/* Formulario */}
              <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <FileText className="w-8 h-8 text-[#0b742f]" />
                  Formulario de solicitud de ingreso
                </h3>
                <p className="text-gray-600 mb-6">Los campos marcados con * son obligatorios</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Success Message */}
                {isSuccess && (
                  <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">¡Formulario enviado exitosamente!</p>
                      <p className="text-sm mt-1">Nos pondremos en contacto pronto.</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitError && (
                  <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-lg">
                    <p className="font-semibold">Error al enviar el formulario</p>
                    <p className="text-sm mt-1">{submitError}</p>
                  </div>
                )}

                

                {/* Tipo de registro */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3">
                    Registrarse como: <span className="text-red-600">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-[#0b742f] cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="tipo"
                        value="docente"
                        checked={formData.tipo === 'docente'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#0b742f]"
                      />
                      <span className="text-gray-800 font-medium">Docente Investigador</span>
                    </label>
                    <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-[#0b742f] cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="tipo"
                        value="grupo"
                        checked={formData.tipo === 'grupo'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#0b742f]"
                      />
                      <span className="text-gray-800 font-medium">Grupo de Investigación</span>
                    </label>
                  </div>
                </div>

                {/* Nombre Completo */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nombre Completo <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su nombre completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                {/* Línea de Investigación */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Línea de Investigación <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="linea"
                    value={formData.linea}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su línea de investigación"
                  />
                </div>

                {/* Institución */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Institución <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="institucion"
                    value={formData.institucion}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su institución"
                  />
                </div>

                {/* Renovación membresía */}
                <div className="bg-emerald-50 rounded-lg border-2 border-emerald-100 p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Renovación membresía</h3>
                  <label className="flex items-start gap-3 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      name="renovacionMembresia"
                      checked={formData.renovacionMembresia}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-gray-700 font-medium">
                      Marcar si esta solicitud corresponde a renovación de membresía.
                    </span>
                  </label>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Número de membresía a renovar
                  </label>
                  <input
                    type="text"
                    name="numeroMembresia"
                    value={formData.numeroMembresia}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-emerald-200 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors bg-white"
                    placeholder="Ingrese el número de membresía"
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Tu mensaje
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors resize-none"
                    placeholder="Mensaje adicional (opcional)"
                  />
                </div>

                {/* Checkbox de aceptación */}
                <div>
                  <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#0b742f] transition-colors">
                    <input
                      type="checkbox"
                      name="acepto"
                      checked={formData.acepto}
                      onChange={handleInputChange}
                      required
                      className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">
                      <span className="text-red-600 font-semibold">* Acepto:</span> Con el envío de este formulario aceptas el tratamiento y gestión de los datos personales según nuestra Política de Privacidad.
                    </span>
                  </label>
                </div>

                {/* Botón de envío */}
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </section>

        {/* Llamada a la Acción Final */}
        <section>
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
            <Users className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Únete a la Red UNADI
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Forma parte de la Red de Cátedras UNESCO para el Aprendizaje y Desarrollo de Iberoamérica y contribuye a la educación inclusiva, equitativa y de calidad en la región.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-white text-[#0b742f] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-200 flex items-center gap-2">
                Registrarme ahora
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="https://www.unesco.org/es/ethics"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-bold hover:bg-white/20 transition-colors duration-200 flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Principios éticos UNESCO
              </a>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default RedUNADI;
